import { ReactNode, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { useShiftStore } from '../stores/shiftStore';
import { shiftApi, restaurantApi } from '../services/api';
import { Grid3X3, ShoppingCart, LogOut, Clock, Megaphone, X, AlertTriangle, Bell, UserX, Printer, CreditCard } from 'lucide-react';
import clsx from 'clsx';
import OfflineIndicator from './OfflineIndicator';
import ChatWidget from './chat/ChatWidget';
import NotificationBell from './NotificationBell';
import TimeClock from './TimeClock';
import PrinterSettingsModal from './PrinterSettingsModal';
import TapToPaySetupModal from './TapToPaySetupModal';
import { isTapToPaySupported, warmUpTapToPay } from '../services/tapToPay';
import { printerService } from '../services/printer';

interface Announcement {
  id: string;
  title: string;
  message: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  createdAt: string;
}

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { employee, logout } = useAuthStore();
  const { status: shiftStatus, liveStats, updateLiveStats, endShift } = useShiftStore();
  const [showEndShiftConfirm, setShowEndShiftConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [showTapToPaySetup, setShowTapToPaySetup] = useState(false);
  const [tapToPayAware, setTapToPayAware] = useState(false);

  // Warm up the reader at launch/foreground on devices that completed setup
  // (Apple reqs 1.5/5.6 — the tap sheet then opens fast at checkout)
  useEffect(() => {
    warmUpTapToPay();
    const onVis = () => {
      if (document.visibilityState === 'visible') warmUpTapToPay();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Surface the Tap to Pay awareness banner when it's supported, not yet set
  // up, and not dismissed. IMPORTANT: this must NOT initialize the Stripe
  // Terminal SDK — doing that on the main screen made the connection-token
  // provider time out (~60s) on devices without the entitlement active. We
  // read a cheap local flag instead; the SDK is only touched when the user
  // actually opens setup or takes a payment.
  useEffect(() => {
    const supported = isTapToPaySupported();
    const dismissed = localStorage.getItem('mise-ttp-aware-dismissed') === '1';
    const setupDone = localStorage.getItem('mise-ttp-setup-done') === '1';
    setTapToPayAware(supported && !dismissed && !setupDone);

    // Apple reqs 3.2/6.2: a full-screen Tap to Pay introduction shown to
    // every eligible user at least once — auto-open the setup modal on the
    // first eligible session (it doubles as the splash/hero screen)
    const splashShown = localStorage.getItem('mise-ttp-splash-shown') === '1';
    if (supported && !setupDone && !splashShown) {
      localStorage.setItem('mise-ttp-splash-shown', '1');
      setShowTapToPaySetup(true);
    }
  }, [showTapToPaySetup]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<string>>(() => {
    const stored = localStorage.getItem('dismissedAnnouncements');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  // Extract context from URL for chat
  const tableId = params.tableId;

  // Adopt the restaurant-wide printer setup (set once in the manager
  // dashboard; every POS device inherits it).
  useEffect(() => {
    if (!employee) return;
    restaurantApi.current().then((res) => {
      if (res.success) {
        printerService.syncFromRestaurant((res.data as any)?.settings?.printers);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

  // Fetch announcements
  const { data: announcementsData } = useQuery({
    queryKey: ['announcements', employee?.primaryRole],
    queryFn: async () => {
      const response = await shiftApi.getAnnouncements(employee?.primaryRole);
      if (response.success && response.data) {
        return response.data as Announcement[];
      }
      return [];
    },
    enabled: !!employee && shiftStatus === 'active',
    refetchInterval: 60000, // Check every minute for new announcements
    staleTime: 30000,
  });

  // Filter out dismissed announcements
  const activeAnnouncements = (announcementsData || []).filter(
    (a: Announcement) => !dismissedAnnouncements.has(a.id)
  );

  // Check for urgent announcements to auto-show
  useEffect(() => {
    const urgentAnnouncements = activeAnnouncements.filter(
      (a: Announcement) => a.priority === 'urgent' || a.priority === 'high'
    );
    if (urgentAnnouncements.length > 0 && !showAnnouncements) {
      setShowAnnouncements(true);
    }
  }, [activeAnnouncements]);

  const dismissAnnouncement = useCallback((id: string) => {
    setDismissedAnnouncements(prev => {
      const updated = new Set(prev);
      updated.add(id);
      localStorage.setItem('dismissedAnnouncements', JSON.stringify([...updated]));
      return updated;
    });
  }, []);

  const dismissAllAnnouncements = useCallback(() => {
    setDismissedAnnouncements(prev => {
      const updated = new Set(prev);
      activeAnnouncements.forEach((a: Announcement) => updated.add(a.id));
      localStorage.setItem('dismissedAnnouncements', JSON.stringify([...updated]));
      return updated;
    });
    setShowAnnouncements(false);
  }, [activeAnnouncements]);

  // Poll for live shift stats every 30 seconds
  const { data: shiftSummary } = useQuery({
    queryKey: ['shift-summary', employee?.id],
    queryFn: async () => {
      if (!employee?.id) return null;
      const response = await shiftApi.getShiftSummary(employee.id);
      if (response.success && response.data) {
        updateLiveStats({
          tablesServed: response.data.stats.tablesServed,
          guestsServed: response.data.stats.guestsServed,
          salesTotalCents: response.data.stats.salesTotalCents,
          tipsTotalCents: response.data.stats.tipsTotalCents,
        });
        return response.data;
      }
      return null;
    },
    enabled: !!employee?.id && shiftStatus === 'active',
    refetchInterval: 30000, // 30 seconds
    staleTime: 15000,
  });

  const handleEndShift = () => {
    endShift();
    navigate('/post-shift');
  };

  const navItems = [
    { path: '/tables', label: 'Tables', icon: Grid3X3 },
    // Could add more nav items like Quick Order, etc.
  ];

  return (
    <div className="h-full flex flex-col bg-slate-100">
      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Header */}
      <header className="bg-primary-900 text-white px-4 py-3 flex items-center justify-between safe-top">
        {/* Who's working, not the app name — the person matters mid-shift. */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-white/15 rounded-xl flex items-center justify-center shrink-0">
            <ShoppingCart className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="font-semibold leading-tight truncate">
              {employee ? `${employee.firstName} ${employee.lastName}` : 'Mise POS'}
            </h1>
            <p className="text-xs text-primary-200 capitalize leading-tight">
              {(employee as any)?.primaryRole || 'Point of Sale'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-sm text-primary-100 bg-white/10 rounded-full px-3 py-1">
            <Clock className="w-3.5 h-3.5" />
            <span className="tabular-nums">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          {/* Course Ready Notifications */}
          <NotificationBell />

          {/* Announcements Button */}
          {activeAnnouncements.length > 0 && (
            <button
              onClick={() => setShowAnnouncements(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors relative"
              title="View Announcements"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center">
                {activeAnnouncements.length}
              </span>
            </button>
          )}
          {isTapToPaySupported() && (
            <button
              onClick={() => setShowTapToPaySetup(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Tap to Pay"
            >
              <CreditCard className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setShowPrinterSettings(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Printers"
          >
            <Printer className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Switch User"
          >
            <UserX className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowEndShiftConfirm(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="End Shift"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Time Clock Bar */}
      {shiftStatus === 'active' && (
        <TimeClock liveStats={liveStats} />
      )}

      {/* Awareness: how an existing merchant learns Tap to Pay is available.
          Shows until set up or dismissed; opens the setup + education flow. */}
      {tapToPayAware && (
        <button
          onClick={() => setShowTapToPaySetup(true)}
          className="w-full bg-primary-50 border-b border-primary-100 px-4 py-2.5 flex items-center gap-2 text-left"
        >
          <CreditCard className="w-4 h-4 text-primary-600 shrink-0" />
          <span className="text-sm text-primary-800 flex-1">
            <span className="font-semibold">Accept cards by tapping them on this iPhone.</span> Tap to set up Tap to Pay.
          </span>
          <span
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              localStorage.setItem('mise-ttp-aware-dismissed', '1');
              setTapToPayAware(false);
            }}
            className="p-1 text-primary-400 hover:text-primary-600"
          >
            <X className="w-4 h-4" />
          </span>
        </button>
      )}

      {/* End Shift Confirmation Modal */}
      {showEndShiftConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2">End Your Shift?</h3>
            <p className="text-slate-600 mb-6">
              You'll be able to review your shift summary and clock out on the next screen.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndShiftConfirm(false)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowEndShiftConfirm(false);
                  handleEndShift();
                }}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-semibold"
              >
                End Shift
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Without Clock Out Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Switch User?</h3>
            <p className="text-slate-600 mb-6">
              Your shift will remain active. Another staff member can log in and continue working.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLogoutConfirm(false);
                  logout();
                  navigate('/');
                }}
                className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-semibold"
              >
                Switch User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcements Modal */}
      {showAnnouncements && activeAnnouncements.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-20">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-bold text-slate-900">Shift Announcements</h3>
              </div>
              <button
                onClick={() => setShowAnnouncements(false)}
                className="p-1 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-3">
              {activeAnnouncements.map((announcement: Announcement) => {
                const priorityColors: Record<string, string> = {
                  urgent: 'bg-red-50 border-red-300 text-red-800',
                  high: 'bg-orange-50 border-orange-300 text-orange-800',
                  normal: 'bg-blue-50 border-blue-300 text-blue-800',
                  low: 'bg-slate-50 border-slate-300 text-slate-700',
                };
                const colorClass = priorityColors[announcement.priority] || priorityColors.normal;

                return (
                  <div
                    key={announcement.id}
                    className={clsx('rounded-xl p-4 border-2 relative', colorClass)}
                  >
                    <button
                      onClick={() => dismissAnnouncement(announcement.id)}
                      className="absolute top-2 right-2 p-1 hover:bg-white/50 rounded"
                      title="Dismiss"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="pr-6">
                      <div className="flex items-center gap-2 mb-1">
                        {(announcement.priority === 'urgent' || announcement.priority === 'high') && (
                          <AlertTriangle className="w-4 h-4" />
                        )}
                        <p className="font-semibold">{announcement.title}</p>
                      </div>
                      <p className="text-sm mt-1 opacity-90">{announcement.message}</p>
                      <p className="text-xs mt-2 opacity-70">
                        {new Date(announcement.createdAt).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-slate-200">
              <button
                onClick={dismissAllAnnouncements}
                className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
              >
                Dismiss All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="bg-white border-t border-slate-200 px-4 py-2 safe-bottom">
        <div className="flex justify-around">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={clsx(
                  'flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors min-w-touch',
                  isActive
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                )}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Printer setup */}
      {showPrinterSettings && <PrinterSettingsModal onClose={() => setShowPrinterSettings(false)} />}
      {showTapToPaySetup && <TapToPaySetupModal onClose={() => setShowTapToPaySetup(false)} />}

      {/* Chat Widget */}
      <ChatWidget
        context={{
          type: 'pos',
          tableId,
        }}
      />
    </div>
  );
}
